import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { ErrorsService } from "common";
import { UserPermissionService } from "../user/user-permission/user-permission.service";
import { UserPermissionData } from "../user/user-permission/user-permission.data";

/**
 * Universal permission guard for route protection based on user permissions.
 *
 * @example
 * // Single permission
 * {
 *   path: 'customer/:id/identity-verification',
 *   canActivate: [BOAuthGuard, PermissionGuard],
 *   data: {
 *     permissionGuard: {
 *       permissions: [readOnly('servicing-customers-identification-data')]
 *     }
 *   }
 * }
 *
 * @example
 * // Multiple permissions
 * {
 *   path: 'customer/:id/admin-edit',
 *   canActivate: [PermissionGuard],
 *   data: {
 *     permissionGuard: {
 *       permissions: [
 *         readWrite('servicing-customers-verification-status'),
 *         readOnly('admin-users')
 *       ],
 *       requireAll: true
 *     }
 *   }
 * }
 */
@Injectable({ providedIn: "root" })
export class PermissionGuard implements CanActivate {
  constructor(
    private readonly userPermissionService: UserPermissionService,
    private readonly errorsService: ErrorsService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> {
    const permissionData: PermissionGuardData = route.data["permissionGuard"];

    if (!permissionData || permissionData?.permissions?.length === 0) {
      console.warn(
        "PermissionGuard: No permissions configured for route",
        state.url,
      );
      this.errorsService.showForbiddenErrorPage();
      return of(false);
    }

    return this.userPermissionService
      .granted(permissionData.permissions, permissionData.requireAll ?? false)
      .pipe(
        map((hasAccess: boolean) => {
          if (!hasAccess) {
            this.errorsService.showForbiddenErrorPage();
          }
          return hasAccess;
        }),
      );
  }
}

export interface PermissionGuardData {
  permissions: UserPermissionData[];
  /**
   * If true, all permissions must be granted (AND logic).
   * If false, at least one permission must be granted (OR logic).
   * @default false
   */
  requireAll?: boolean;
}
